import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AvatarImg from "../../../../images/profile-avatar.png";
import StartImg from "../../../../images/star.svg";
import IconButton from "../../../iconButton";
import {
  AdjustmentsVerticalIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DevicePhoneMobileIcon,
  EnvelopeIcon,
  MapPinIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import moment from "moment";
import {
  formatAddress,
  formatDate,
  formatPhoneNumber,
  generateFullImageUrl,
  getIsMHCName
} from "../../../../utilities/utils";
import Rating from "../../../rating";
import { professionalReviewsItems } from "../adminData";
import GlobalTableDropDown from "../GlobalTableDropDown";
import UserInvoicesModal from "../../../modal/UserInvoicesModal";
import {
  useGetAllUserInvoicesQuery,
  useLazyGetAllUserInvoicesQuery,
} from "../../../../store/admin/invoices";
import { useGetProfessionalMetricsDataQuery } from "../../../../store/admin/professionalUserInfo";
import { useImpersonateProcessStartMutation } from "../../../../store/admin/impersonate";
import Loader from "../../../commons/Loader";
import { toast } from "react-toastify";
import { useGetAuthTwilioChatTokenMutation } from "../../../../store/authTwilioToken";
import { getDisplayProfessionalName } from "../../../../utilities/professionalUtils";

const ProfessionalReviews = ({ userInfoDetail }) => {
  const navigate = useNavigate();
  const [showMetric, setShowMetrics] = useState(false);
  const [showMenu, setShowMenu] = useState(null);
  const [professional, setProfessional] = useState(userInfoDetail);
  const [twilioToken] = useGetAuthTwilioChatTokenMutation();

  const [showInvoicesModal, setShowInvoicesModal] = useState(false);
  const [allInvoicesData, setAllInvoicesData] = useState();

  const { data: allInvoices, isLoading: allInvoicesLoader } =
    useGetAllUserInvoicesQuery({ id: userInfoDetail.id });

  const {
    data: professionalMetricsData,
    isLoading: professionalMetricsDataLoader,
  } = useGetProfessionalMetricsDataQuery({ id: userInfoDetail.id });

  const [
    getUpdatedUserInvocies,
    { data: updatedUserInvociesData, isLoading: updatedUserInvociesIsLoading },
  ] = useLazyGetAllUserInvoicesQuery();

  const [
    startImpersonationProcess,
    {
      isSuccess: impersonationSuccess,
      isLoading: impersonationLoader,
      data: impersonateResponse,
    },
  ] = useImpersonateProcessStartMutation();

  useEffect(() => {
    if (allInvoices) {
      setAllInvoicesData(allInvoices);
    }
  }, [allInvoices]);

  const isMHC = professional?.professions[0]?.name == "Mental Health Counselor";


  useEffect(() => {
    if (updatedUserInvociesData) {
      setAllInvoicesData(updatedUserInvociesData);
    }
  }, [updatedUserInvociesData]);

  useEffect(() => {
    if (impersonationSuccess && !impersonationLoader) {
      localStorage.setItem("token", impersonateResponse?.token);
      localStorage.setItem("isImpersonate", true);
      assignedTwilioToken()
        .then((twilioChatToken) => {
          localStorage.setItem("twilioToken", twilioChatToken);
          navigate("/dashboard/professional-impersonate");
        })
        .catch((error) => {
          toast.error("Error fetching Twilio token:");
        });
    }
  }, [impersonationSuccess]);

  const assignedTwilioToken = async () => {
    const newTwilioToken = await twilioToken();
    return newTwilioToken.data.token;
  };

  const currentDate = moment();
  const createdDate = moment(professional?.created_at);
  const account_age = currentDate.diff(createdDate, "days");

  // const formattedRegions = professional?.regions2.map(
  //   (region) => `${region.zip_code} + ${region.miles} Miles`
  // );
  const formattedRegions = professional?.regions2?.map(
    (region) =>
      `${region?.profession?.name} ${region?.zip_code} + ${region?.miles} Miles`
  )

  const formattedVirtualRegions = professional?.states?.map(
    (state) =>
      `${state?.name} (${state?.code})`
  ).sort().join(", ");
  const hasDrPrefix = professional?.professions.some(
    (profession) => profession.add_dr_prefix === 1
  );

  const professionNames =
    professional &&
    professional?.professions
      ?.map((profession) => {
        return profession.name;
      })
      .flat();

  const handleProfessionalImpersonation = () => {
    startImpersonationProcess(professional?.id);
  };

  const leadServicesNames =
    professional &&
    professional?.professions
      ?.map((profession) => {
        return profession.services;
      })
      .flat();

  const handleMetrics = () => {
    setShowMetrics(!showMetric);
  };
  const handleMenuItems = (userId, flag) => {
    if (!flag) {
      setShowMenu(userId.id);
    } else {
      setShowMenu(null);
    }
  };

  const handleProfessionalSettings = () => {
    navigate("/admin-dashboard/professional-profile", {
      state: {
        professionalId: professional?.id,
      },
    });
  };

  const openGoogleMaps = (professional) => {
    const address = professional?.office_address_1
      ? professional.office_address_1
      : formatAddress(
          professional?.city,
          professional?.state,
          professional?.postal_code
        );

    const encodedAddress = encodeURIComponent(address);
    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;

    window.open(googleMapsUrl, "_blank");
  };

  return (
    <>
      {impersonationLoader ? (
        <Loader isLoading={true} />
      ) : (
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 xxl:col-span-8">
            <div className="py-[22px] px-5 border border-solid border-[#EBEFF3] bg-white  rounded-md rounded-tl-0 mb-[14px] md:mb-6">
              <div className="flex justify-between flex-col 3xl:flex-row gap-2">
                <div className="3xl:max-w-[calc(100%-140px)] w-full">
                  <div className="flex gap-5 mb-5 md:mb-0">
                    <div className="relative w-[90px] md:w-[115px] h-[90px] md:h-[115px] rounded-[50%]">
                      <img
                        src={
                          professional?.photo
                            ? generateFullImageUrl(professional?.photo)
                            : AvatarImg
                        }
                        alt=""
                        className="w-[90px] md:w-[115px] h-[90px] md:h-[115px] rounded-[50%] bg-[#485868]"
                      />
                      <div className="absolute right-1 md:right-2 bottom-1 md:bottom-2 w-4 md:w-5 h-4 md:h-5">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="none"
                        >
                          <circle
                            cx="10"
                            cy="10"
                            r="9"
                            fill="#16CA7C"
                            stroke="white"
                            strokeWidth="2"
                          />
                        </svg>
                      </div>
                    </div>
                    <div className="w-[calc(100%-110px)] md:w-[calc(100%-135px)]">
                      <div className="flex justify-between">
                        <div>
                          <div className="flex items-center gap-4">
                            <h4 className="text-base xl:text-xl font-bold leading-[180%] text-[#04091E]">
                            {professional&&getDisplayProfessionalName(professional,hasDrPrefix)}
                            </h4>
                            <div className="hidden md:flex items-center gap-0.5">
                              <Rating rating={professional?.rating} />
                            </div>
                          </div>
                          <span className="text-sm xl:text-base font-normal leading-[160%] text-[#485868] inline-block mb-[10px]">
                            {isMHC ?  getIsMHCName(professional?.professions[0]) : professionNames?.join(", ") || "---"}
                          </span>
                          <div className="flex md:hidden items-center">
                            <Rating rating={professional?.rating} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="md:pl-[135px] md:-mt-8">
                    <div className="flex items-start xl:items-center flex-col xl:flex-row gap-3 xl:gap-[30px] mb-3">
                      <a
                        href={`tel:${
                          professional?.cell_phone_number || "0000000000"
                        }`}
                        className="text-sm font-medium leading-[160%] text-customGray flex items-center gap-1.5"
                      >
                        <span>
                          <DevicePhoneMobileIcon className="w-5 h-5 text-primary" />
                        </span>
                        {(professional?.cell_phone_number &&
                          formatPhoneNumber(
                            professional?.cell_phone_number
                          )) ||
                          "(561) 425-7033"}
                      </a>
                      <a
                        href={`mailto:${professional?.email}`}
                        className="text-sm font-medium leading-[160%] text-customGray flex items-center gap-1.5"
                        style={{ lineBreak: "anywhere" }}
                      >
                        <span>
                          <EnvelopeIcon className="w-5 h-5 text-primary" />
                        </span>
                        {professional?.email}
                      </a>
                    </div>
                    <button
                      className="text-sm font-medium leading-[160%] text-customGray flex items-center gap-1.5"
                      onClick={() => openGoogleMaps(professional)}
                    >
                      <span>
                        <MapPinIcon className="w-5 h-5 text-primary" />
                      </span>
                      {professional?.office_address_1
                        ? professional?.office_address_1
                        : formatAddress(
                            professional?.city,
                            professional?.state,
                            professional?.postal_code
                          )}
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-3 3xl:block mt-6 3xl:mt-0">
                  <IconButton
                    icon={UserIcon}
                    iconClasses="w-6 h-6"
                    title="Impersonate"
                    classes="border border-primary rounded-[10px] bg-primary flex gap-1.5 items-center justify-center max-w-[146px] 3xl:max-w-[128px] w-full px-2 text-sm font-black leading-[160%] text-white h-10 3xl:mb-3"
                    onClick={handleProfessionalImpersonation}
                  />
                  <IconButton
                    icon={AdjustmentsVerticalIcon}
                    iconClasses="w-6 h-6"
                    title="Edit"
                    classes="border border-primary rounded-[10px] bg-white flex gap-1.5 items-center justify-center max-w-[146px] 3xl:max-w-[128px] w-full px-2 text-sm font-black leading-[160%] text-primary h-10"
                    onClick={handleProfessionalSettings}
                  />
                </div>
              </div>
            </div>
            <div>
              <h4 className="text-lg md:text-xl font-bold leading-[180%] text-customGray mb-5">
                Reviews
              </h4>
              <div className="py-6 px-5 border border-solid border-[#EBEFF3] bg-white rounded-md flex md:items-center flex-col md:flex-row gap-4 md:gap-5 mb-[14px]">
                <div className="flex items-center gap-[18px]">
                  <img
                    src={AvatarImg}
                    alt=""
                    className="w-[70px] h-[70px] rounded-[50%] bg-[#485868]"
                  />
                  <div className="flex items-center justify-between md:hidden w-[calc(100%-80px)]">
                    <div className="flex items-center gap-0.5 mb-[10px]">
                      <img src={StartImg} alt="" />
                      <img src={StartImg} alt="" />
                      <img src={StartImg} alt="" />
                      <img src={StartImg} alt="" />
                      <img src={StartImg} alt="" />
                    </div>
                    <GlobalTableDropDown
                      data={professional}
                      showMenu={showMenu === professional.id}
                      callBack={handleMenuItems}
                      // isLastIndex={index == professionalTable.length - 2}
                      menuItems={professionalReviewsItems}
                    />
                  </div>
                </div>
                <div className="md:w-[calc(100%-90px)]">
                  <div className="hidden md:flex items-center justify-between">
                    <div className="flex items-center gap-0.5 mb-[10px]">
                      <img src={StartImg} alt="" />
                      <img src={StartImg} alt="" />
                      <img src={StartImg} alt="" />
                      <img src={StartImg} alt="" />
                      <img src={StartImg} alt="" />
                    </div>
                    <GlobalTableDropDown
                      data={professional}
                      showMenu={showMenu === professional.id}
                      callBack={handleMenuItems}
                      // isLastIndex={index == professionalTable.length - 2}
                      menuItems={professionalReviewsItems}
                    />
                  </div>
                  <h6 className="text-base font-bold leading-[160%] text-customGray mb-[10px]">
                    I recommend Ryan as an excellent professional
                  </h6>
                  <p className="text-sm font-normal leading-[170%] text-[#485868] max-w-[600px] mb-[10px]">
                    Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed
                    do eiusmod tempor incididunt ut labore et dolore magna
                    aliqua. Ut enim ad minim veniam, quis nostrud exercitation
                    ullamco laboris nisi ut aliquip ex ea commodo consequat.
                    Duis aute irure dolor in reprehenderit in voluptate velit
                    esse cillum.
                  </p>
                  <div className="flex items-center gap-[7px] md:gap-2.5 text-sm font-normal leading-[140%] text-[#485868]">
                    <b>Gretchen Philips</b>
                    &#x2022;
                    <span>XX/XX/XXXX</span>
                    &#x2022;
                    <span>XX:XX AM</span>
                  </div>
                </div>
              </div>
              <div className="py-6 px-5 border border-solid border-[#EBEFF3] bg-white rounded-md flex md:items-center flex-col md:flex-row gap-4 md:gap-5">
                <div className="flex items-center gap-[18px]">
                  <img
                    src={AvatarImg}
                    alt=""
                    className="w-[70px] h-[70px] rounded-[50%] bg-[#485868]"
                  />
                  <div className="flex items-center justify-between md:hidden w-[calc(100%-80px)]">
                    <div className="flex items-center gap-0.5 mb-[10px]">
                      <img src={StartImg} alt="" />
                      <img src={StartImg} alt="" />
                      <img src={StartImg} alt="" />
                      <img src={StartImg} alt="" />
                      <img src={StartImg} alt="" />
                    </div>
                    <GlobalTableDropDown
                      data={professional}
                      showMenu={showMenu === professional.id}
                      callBack={handleMenuItems}
                      // isLastIndex={index == professionalTable.length - 2}
                      menuItems={professionalReviewsItems}
                    />
                  </div>
                </div>
                <div className="md:w-[calc(100%-90px)]">
                  <div className="hidden md:flex items-center justify-between">
                    <div className="flex items-center gap-0.5 mb-[10px]">
                      <img src={StartImg} alt="" />
                      <img src={StartImg} alt="" />
                      <img src={StartImg} alt="" />
                      <img src={StartImg} alt="" />
                      <img src={StartImg} alt="" />
                    </div>
                    <GlobalTableDropDown
                      data={professional}
                      showMenu={showMenu === professional.id}
                      callBack={handleMenuItems}
                      // isLastIndex={index == professionalTable.length - 2}
                      menuItems={professionalReviewsItems}
                    />
                  </div>
                  <h6 className="text-base font-bold leading-[160%] text-customGray mb-[10px]">
                    I recommend Ryan as an excellent professional
                  </h6>
                  <p className="text-sm font-normal leading-[170%] text-[#485868] max-w-[600px] mb-[10px]">
                    Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed
                    do eiusmod tempor incididunt ut labore et dolore magna
                    aliqua. Ut enim ad minim veniam, quis nostrud exercitation
                    ullamco laboris nisi ut aliquip ex ea commodo consequat.
                    Duis aute irure dolor in reprehenderit in voluptate velit
                    esse cillum.
                  </p>
                  <div className="flex items-center gap-[7px] md:gap-2.5 text-sm font-normal leading-[140%] text-[#485868]">
                    <b>Gretchen Philips</b>
                    &#x2022;
                    <span>XX/XX/XXXX</span>
                    &#x2022;
                    <span>XX:XX AM</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-span-12 xxl:col-span-4 xl:-mt-10">
            <div>
              <IconButton
                icon={showMetric ? ChevronUpIcon : ChevronDownIcon}
                onClick={handleMetrics}
                iconClasses="w-6 h-6"
                title={
                  showMetric
                    ? "hide metrics for admin"
                    : "Show metrics for admin"
                }
                classes={`border border-primary rounded-[10px] flex gap-1.5 items-center justify-center w-full px-2 text-sm font-black leading-[160%] h-10 mb-6 uppercase ${
                  showMetric ? "bg-white text-primary" : "bg-primary text-white"
                }`}
              />
              {showMetric && (
                <div>
                  <div className="grid grid-cols-12 gap-4 md:gap-6 mb-4 md:mb-6">
                    <div className="col-span-12 md:col-span-6 p-5 border border-solid border-[#EBEFF3] bg-white rounded-md flex items-start justify-between md:block">
                      <div className="w-[48%] md:w-auto">
                        <span
                          className="w-[90px] h-[27px] rounded-md text-xs font-bold leading-[160%] text-white flex items-center justify-center mb-3.5"
                          style={{
                            backgroundColor: professional?.status?.color,
                          }}
                        >
                          {professional?.status?.name}
                        </span>
                        <h6 className="text-lg md:text-xl font-bold leading-[160%] text-customGray">
                          {account_age} {account_age === 1 ? "Day" : "Days"}
                        </h6>
                        <span className="w-[30px] h-0.5 block bg-primary my-2"></span>
                        <p className="text-xs font-bold leading-[160%] uppercase text-[#485868]">
                          Account Age
                        </p>
                      </div>
                      <div className="bg-[#EBEFF3] w-0.5 md:w-full h-full md:h-0.5 mx-8 md:mx-0 md:mt-4 md:mb-3"></div>
                      <div className="w-[48%] md:w-auto">
                        <p className="text-xs font-bold leading-[160%] uppercase text-[#485868]">
                          Enrollment Date:{" "}
                          <b className="text-customGray underline block">
                            {formatDate(professional?.created_at)}
                          </b>
                        </p>
                        <div className="bg-[#EBEFF3] w-0.5 md:w-full h-full md:h-0.5 mx-8 md:mx-0 md:mt-4 md:mb-3"></div>
                        <a
                          href="#"
                          onClick={() => setShowInvoicesModal(true)}
                          className="text-xs font-bold leading-[160%] text-primary"
                        >
                          View Invoices
                        </a>
                      </div>
                    </div>
                    <div className="col-span-12 md:col-span-6 grid grid-cols-2 md:grid-cols-1 gap-4 md:gap-6">
                      <div className="p-5 border border-solid border-[#EBEFF3] bg-white rounded-md">
                        <h6 className="text-lg md:text-xl font-bold leading-[160%] text-customGray">
                          {professional?.credits}
                        </h6>
                        <span className="w-[30px] h-0.5 block bg-[#7D34D8] my-2"></span>
                        <p className="text-xs font-bold leading-[160%] uppercase text-[#485868]">
                          CREDIT BALANCE
                        </p>
                      </div>
                      <div className="p-5 border border-solid border-[#EBEFF3] bg-white rounded-md">
                        <h6 className="text-lg md:text-xl font-bold leading-[160%] text-customGray">
                          {professional?.daily_cap}
                        </h6>
                        <span className="w-[30px] h-0.5 block bg-[#D834A0] my-2"></span>
                        <p className="text-xs font-bold leading-[160%] uppercase text-[#485868]">
                          Daily Lead LIMIT
                        </p>
                      </div>
                    </div>
                    <div className="col-span-6 p-5 border border-solid border-[#EBEFF3] bg-white rounded-md">
                      <h6 className="text-lg md:text-xl font-bold leading-[160%] text-customGray">
                        ${professionalMetricsData?.data?.lifetime_revenue}
                      </h6>
                      <span className="w-[30px] h-0.5 block bg-[#16CA7C] my-2"></span>
                      <p className="text-xs font-bold leading-[160%] uppercase text-[#485868]">
                        Lifetime Revenue
                      </p>
                    </div>
                    <div className="col-span-6 p-5 border border-solid border-[#EBEFF3] bg-white rounded-md">
                      <h6 className="text-lg md:text-xl font-bold leading-[160%] text-customGray">
                        {professionalMetricsData?.data?.lifetime_credits}
                      </h6>
                      <span className="w-[30px] h-0.5 block bg-[#16CA7C] my-2"></span>
                      <p className="text-xs font-bold leading-[160%] uppercase text-[#485868]">
                        Lifetime CREDITS
                      </p>
                    </div>
                    <div className="col-span-6 p-5 border border-solid border-[#EBEFF3] bg-white rounded-md">
                      <h6 className="text-lg md:text-xl font-bold leading-[160%] text-customGray">
                      {professionalMetricsData?.data?.lifetime_credit_spent}
                      </h6>
                      <span className="w-[30px] h-0.5 block bg-[#EF2121] my-2"></span>
                      <p className="text-xs font-bold leading-[160%] uppercase text-[#485868]">
                        Lifetime Credits spent
                      </p>
                    </div>
                    <div className="col-span-6 p-5 border border-solid border-[#EBEFF3] bg-white rounded-md">
                      <h6 className="text-lg md:text-xl font-bold leading-[160%] text-customGray">
                        $
                        {professionalMetricsData?.data?.lifetime_cost_per_credit?.toFixed(
                          2
                        )}
                      </h6>
                      <span className="w-[30px] h-0.5 block bg-[#EF2121] my-2"></span>
                      <p className="text-xs font-bold leading-[160%] uppercase text-[#485868]">
                        Lifetime Cost Per Credit
                      </p>
                    </div>
                    <div className="col-span-6 p-5 border border-solid border-[#EBEFF3] bg-white rounded-md">
                      <div>
                        <h6 className="text-lg md:text-xl font-bold leading-[160%] text-customGray">
                          {
                            professionalMetricsData?.data
                              ?.total_credit_refund_requests
                          }
                        </h6>
                        <span className="w-[30px] h-0.5 block bg-[#7D34D8] my-2"></span>
                        <p className="text-xs font-bold leading-[160%] uppercase text-[#485868]">
                          Total Credit Refund Requests
                        </p>
                      </div>
                      <div className="bg-[#EBEFF3] w-full h-0.5 my-5"></div>
                      <div>
                        <h6 className="text-lg md:text-xl font-bold leading-[160%] text-customGray">
                          {professionalMetricsData?.data?.percentage_to_matches?.toFixed(
                            2
                          )}
                          %
                        </h6>
                        <span className="w-[30px] h-0.5 block bg-[#7D34D8] my-2"></span>
                        <p className="text-xs font-bold leading-[160%] uppercase text-[#485868]">
                          % to Matches
                        </p>
                      </div>
                    </div>
                    <div className="col-span-6 p-5 border border-solid border-[#EBEFF3] bg-white rounded-md">
                      <div>
                        <h6 className="text-lg md:text-xl font-bold leading-[160%] text-customGray">
                          {
                            professionalMetricsData?.data
                              ?.total_credit_refund_requests_approved
                          }
                        </h6>
                        <span className="w-[30px] h-0.5 block bg-[#7D34D8] my-2"></span>
                        <p className="text-xs font-bold leading-[160%] uppercase text-[#485868]">
                          Total Credit Refund Requests Approved
                        </p>
                      </div>
                      <div className="bg-[#EBEFF3] w-full h-0.5 my-5"></div>
                      <div>
                        <h6 className="text-lg md:text-xl font-bold leading-[160%] text-customGray">
                          {professionalMetricsData?.data?.percentage_to_requests?.toFixed(
                            2
                          )}
                          %
                        </h6>
                        <span className="w-[30px] h-0.5 block bg-[#7D34D8] my-2"></span>
                        <p className="text-xs font-bold leading-[160%] uppercase text-[#485868]">
                          % to Requests
                        </p>
                      </div>
                    </div>
                    <div className="col-span-12 px-5 py-[22px] border border-solid border-[#EBEFF3] bg-white rounded-md">
                      <div className="rounded-[3px] bg-[#F1F4F6] grid grid-cols-2 gap-5 py-2.5 px-3.5">
                        <span className="text-xs font-bold leading-[160%] text-customGray uppercase block">
                        Local Region(s):
                        </span>
                        <p className="text-sm font-normal leading-[160%] text-[#485868]">
                          {formattedRegions?.map((region, index) => (
                            <span key={index}>
                              {region}
                              <br />
                            </span>
                          ))}
                        </p>
                      </div>
                      {professional?.states?.length ?
                      <div className="rounded-[3px] bg-[#F1F4F6] grid grid-cols-2 gap-5 py-2.5 px-3.5">
                        <span className="text-xs font-bold leading-[160%] text-customGray uppercase block">
                        Virtual Region(s):
                        </span>
                        <p className="text-sm font-normal leading-[160%] text-[#485868]">
                            <span>
                              {formattedVirtualRegions}
                              <br />
                            </span>
                        </p>
                      </div> : null}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="p-5 border border-solid border-[#EBEFF3] bg-white rounded-md mb-6">
              <h6 className="text-sm md:text-base font-bold leading-[160%] text-customGray mb-[14px]">
                Qualifications:
              </h6>
              <ul className="qualifications-list">
                {professional && professional?.practice_since && (
                  <li className="py-[10px] px-[14px] rounded-[3px] flex items-center gap-5">
                    <h6 className="w-[136px] text-xs font-bold leading-[160%] uppercase text-customGray">
                      Years in Practice:
                    </h6>
                    <span className="text-sm font-normal leading-[160%] text-[#485868]">
                      {professional && professional?.practice_since !== null
                        ? `${
                            moment().format("YYYY") -
                            professional?.practice_since
                          } years`
                        : "10 years"}
                    </span>
                  </li>
                )}
                {professional?.insuranceCarriers?.length > 0 && (
                  <li className="py-[10px] px-[14px] rounded-[3px] flex items-center gap-5">
                    <h6 className="w-[136px] text-xs font-bold leading-[160%] uppercase text-customGray">
                      Accepts Insurance:
                    </h6>
                    <span className="text-xs font-bold leading-[160%] text-white rounded-[3px] bg-[#16CA7C] px-[9px] uppercase">
                      Yes
                    </span>
                  </li>
                )}
                {professional && professional?.license_number && (
                  <li className="py-[10px] px-[14px] rounded-[3px] flex items-center gap-5">
                    <h6 className="w-[136px] text-xs font-bold leading-[160%] uppercase text-customGray">
                      license # / State:
                    </h6>
                    <span className="text-sm font-normal leading-[160%] text-[#485868]">
                      {professional?.license_number} / {professional?.state}
                    </span>
                  </li>
                )}
                {professional?.profile_percentage === "100" && (
                  <li className="py-[10px] px-[14px] rounded-[3px] flex items-center gap-5">
                    <h6 className="w-[136px] text-xs font-bold leading-[160%] uppercase text-customGray">
                      Achievements:
                    </h6>
                    <ul className="flex items-center gap-[10px]">
                      <li>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="32"
                          height="32"
                          viewBox="0 0 32 32"
                          fill="none"
                        >
                          <path
                            d="M16 32C24.8366 32 32 24.8366 32 16C32 7.16344 24.8366 0 16 0C7.16344 0 0 7.16344 0 16C0 24.8366 7.16344 32 16 32Z"
                            fill="#FFD964"
                          />
                          <path
                            d="M31.9992 16.0002C31.9992 15.7161 31.9915 15.4339 31.9769 15.1535L24.5881 7.76465L7.76367 24.5891L15.1525 31.9779C15.4329 31.9925 15.7152 32.0002 15.9992 32.0002C24.8358 32.0002 31.9992 24.8368 31.9992 16.0002Z"
                            fill="#F3CA4A"
                          />
                          <path
                            d="M15.9993 27.9C22.5713 27.9 27.899 22.5723 27.899 16.0003C27.899 9.42826 22.5713 4.10059 15.9993 4.10059C9.42728 4.10059 4.09961 9.42826 4.09961 16.0003C4.09961 22.5723 9.42728 27.9 15.9993 27.9Z"
                            fill="#48A5FF"
                          />
                          <path
                            d="M27.8994 16C27.8994 22.5718 22.5719 27.8993 16 27.8993V4.10059C22.5719 4.10059 27.8994 9.42809 27.8994 16Z"
                            fill="#1E90FE"
                          />
                          <path
                            d="M15.2254 21.6182L8.3418 15.5138L11.0372 12.4743L15.0791 16.0586L20.8384 10.3828L23.69 13.2764L15.2254 21.6182Z"
                            fill="white"
                          />
                          <path
                            d="M23.69 13.2759L16 20.8547V15.1509L20.8388 10.3828L23.69 13.2759Z"
                            fill="#EAE1DC"
                          />
                        </svg>
                      </li>
                      <li>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="32"
                          height="32"
                          viewBox="0 0 32 32"
                          fill="none"
                        >
                          <path
                            d="M16 32C24.8366 32 32 24.8366 32 16C32 7.16344 24.8366 0 16 0C7.16344 0 0 7.16344 0 16C0 24.8366 7.16344 32 16 32Z"
                            fill="#FF6C52"
                          />
                          <path
                            d="M26.5127 13.1528L15.9995 2.63965L8.23633 26.5323L13.5111 31.8071C14.322 31.9338 15.1531 31.9995 15.9995 31.9995C24.8361 31.9995 31.9995 24.8361 31.9995 15.9995C31.9995 15.7261 31.9926 15.4543 31.9791 15.1843L28.5606 11.7658L26.5127 13.1528Z"
                            fill="#EF2121"
                          />
                          <path
                            d="M16.0006 2.63965L19.8822 10.5046L28.5617 11.7658L22.2811 17.8879L23.7638 26.5324L16.0006 22.451L8.23733 26.5324L9.72002 17.8879L3.43945 11.7658L12.119 10.5046L16.0006 2.63965Z"
                            fill="#FFE289"
                          />
                          <path
                            d="M16 2.63965L19.8816 10.5046L28.5611 11.7658L22.2806 17.8879L23.7632 26.5324L16 22.451V2.63965Z"
                            fill="#FFD912"
                          />
                        </svg>
                      </li>
                      <li>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="32"
                          height="32"
                          viewBox="0 0 32 32"
                          fill="none"
                        >
                          <path
                            d="M16 32C24.8366 32 32 24.8366 32 16C32 7.16344 24.8366 0 16 0C7.16344 0 0 7.16344 0 16C0 24.8366 7.16344 32 16 32Z"
                            fill="#7D34D8"
                          />
                          <path
                            d="M20.8046 7.42349L18.0511 4.66992V6.20917L16.5734 6.14761L19.9028 9.47705L8.77088 13.5162L7.24406 11.9894V14.5842L4.5625 14.6709L8.45838 18.5668V19.6919H6.97025L18.9972 31.7189C25.9678 30.3979 31.3377 24.5561 31.9426 17.3576L20.7326 6.14761L20.8046 7.42349Z"
                            fill="#6014BD"
                          />
                          <path
                            d="M16.0006 17.045L6.75977 14.6852L11.2685 8.8291L19.6549 11.7572L16.0006 17.045Z"
                            fill="#E2D5FF"
                          />
                          <path
                            d="M25.2409 14.6852H16L17.2959 10.5854L20.7321 8.8291L25.2409 14.6852Z"
                            fill="#E2D5FF"
                          />
                          <path
                            d="M16.0006 25.6718L6.75977 14.6855H16.0006L20.6211 16.3702L16.0006 25.6718Z"
                            fill="#CDB8FB"
                          />
                          <path
                            d="M16 25.6718V14.6855H25.2409L16 25.6718Z"
                            fill="#8F77EF"
                          />
                          <path
                            d="M11.2676 8.8291L15.9997 14.6852V8.8291H11.2676Z"
                            fill="#CDB8FB"
                          />
                          <path
                            d="M20.7321 8.8291L16 14.6852V8.8291H20.7321Z"
                            fill="#8F77EF"
                          />
                          <path
                            d="M21.1452 8.41615L20.7323 6.14746L20.3195 8.41615L18.0508 8.82896L20.3195 9.24184L20.7323 11.5105L21.1452 9.24184L23.4138 8.82896L21.1452 8.41615Z"
                            fill="#E9EDF0"
                          />
                          <path
                            d="M18.2794 5.92011L18.0519 4.66992L17.8244 5.92011L16.5742 6.14761L17.8244 6.37511L18.0519 7.6253L18.2794 6.37511L19.5296 6.14761L18.2794 5.92011Z"
                            fill="#E9EDF0"
                          />
                          <path
                            d="M8.67589 19.4641L8.44839 18.2139L8.22089 19.4641L6.9707 19.6916L8.22089 19.9191L8.44839 21.1692L8.67589 19.9191L9.92608 19.6916L8.67589 19.4641Z"
                            fill="#E9EDF0"
                          />
                          <path
                            d="M7.65687 14.2579L7.244 11.9893L6.83119 14.2579L4.5625 14.6708L6.83119 15.0836L7.244 17.3523L7.65687 15.0836L9.92556 14.6708L7.65687 14.2579Z"
                            fill="white"
                          />
                        </svg>
                      </li>
                    </ul>
                  </li>
                )}
              </ul>
            </div>
            {((professional && leadServicesNames?.length > 0) ||
              professional?.insuranceCarriers?.length > 0 ||
              professional?.modalities?.length > 0|| professional?.professions?.length) && (
              <div className="p-5 border border-solid border-[#EBEFF3] bg-white rounded-md">
                {!isMHC&&leadServicesNames?.length > 0 && (
                  <div className="public-service-card">
                    <div className="">
                      <h6 className="text-sm md:text-base font-bold leading-[160%] text-customGray mb-[14px]">
                        Services Offered
                      </h6>
                      <ul className="flex items-center gap-x-2.5 gap-y-3 flex-wrap">
                        {leadServicesNames?.map((service) => (
                          <li
                            key={service.id}
                            className="text-xs font-bold leading-[160%] uppercase text-[#485868] py-[10px] px-3 border border-solid border-[#EBEFF3] bg-white rounded-[40px]"
                          >
                            {service.name}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
                  {isMHC && (
                  <div className="public-service-card">
                    <div className="">
                      <h6 className="text-sm md:text-base font-bold leading-[160%] text-customGray mb-[14px]">
                        Specializations
                      </h6>
                      <ul className="flex items-center gap-x-2.5 gap-y-3 flex-wrap">
                        {professional?.professions.map(
                          (specialty, index) =>
                            index !== 0 && (
                              <li
                                key={specialty.id}
                                className="text-xs font-bold leading-[160%] uppercase text-[#485868] py-[10px] px-3 border border-solid border-[#EBEFF3] bg-white rounded-[40px]"
                              >
                                {specialty.name}
                              </li>
                            )
                        )}
                      </ul>
                    </div>
                  </div>
                )}
                {professional?.modalities?.length > 0 && (
                  <div className="public-service-card">
                    <div className="">
                      <h6 className="text-sm md:text-base font-bold leading-[160%] text-customGray mb-[14px]">
                        Modalities
                      </h6>
                      <ul className="flex items-center gap-x-2.5 gap-y-3 flex-wrap">
                        {professional?.modalities.map((modality) => (
                          <li
                            key={modality.id}
                            className="text-xs font-bold leading-[160%] uppercase text-[#485868] py-[10px] px-3 border border-solid border-[#EBEFF3] bg-white rounded-[40px]"
                          >
                            {modality.name}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
                {professional?.insuranceCarriers?.length > 0 && (
                  <div className="public-service-card">
                    <h6 className="text-sm md:text-base font-bold leading-[160%] text-customGray mb-[14px]">
                      Insurance Carriers Accepted
                    </h6>
                    <ul className="flex items-center gap-x-2.5 gap-y-3 flex-wrap">
                      {professional?.insuranceCarriers.map((carrier) => (
                        <li
                          key={carrier.id}
                          className="text-xs font-bold leading-[160%] uppercase text-[#485868] py-[10px] px-3 border border-solid border-[#EBEFF3] bg-white rounded-[40px]"
                        >
                          {carrier.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
          <UserInvoicesModal
            showModal={showInvoicesModal}
            setShowModal={setShowInvoicesModal}
            allInvoicesData={allInvoicesData}
            userInfoDetail={professional}
            getUpdateData={(page, perPage) => {
              getUpdatedUserInvocies({
                id: professional.id,
                page,
                perPage,
              });
            }}
          />
        </div>
      )}
    </>
  );
};

export default ProfessionalReviews;
